
import React, { useState, useEffect } from 'react';
import { Icons } from '../ui/Icons';
import { database } from '../../services/database';
import { TaskInstance, TaskSubTaskInstance } from '../../types';
import { toast } from 'react-hot-toast';
import { safeFormat } from '../../utils/safeDateFns';

interface TaskExecutionViewProps {
    instance: TaskInstance;
    staffName: string;
    isAdmin?: boolean;
    onBack: () => void;
    onFinalized: () => void;
}

const TaskExecutionView: React.FC<TaskExecutionViewProps> = ({ instance, staffName, isAdmin = false, onBack, onFinalized }) => {
    const [task, setTask] = useState<TaskInstance>(instance);
    const [isSaving, setIsSaving] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [expandedSteps, setExpandedSteps] = useState<{ [index: number]: boolean }>({});

    useEffect(() => {
        setTask(instance);
        // Expand first incomplete step by default
        const firstIncomplete = instance.subTasks.findIndex(st => !st.isDone);
        if (firstIncomplete !== -1) {
            setExpandedSteps({ [firstIncomplete]: true });
        }
    }, [instance]);

    const toggleStepExpansion = (index: number) => {
        setExpandedSteps(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const checkStepCompletion = (step: TaskSubTaskInstance) => {
        if (!step.subSteps || step.subSteps.length === 0) return step.isDone;
        
        const requiredSubSteps = step.subSteps.filter(ss => ss.required);
        if (requiredSubSteps.length === 0) {
            // Task says: "A step is completed ONLY IF: All required sub-steps are checked."
            // If none are required, but there are sub-steps, we require ALL of them to be done 
            // to consider the step finished, matching intuitive checklist behavior.
            return step.subSteps.every(ss => ss.done);
        }
        // Strict requirement: all sub-steps marked as required must be done
        return requiredSubSteps.every(ss => ss.done);
    };

    const saveProgress = async (updatedTask: TaskInstance) => {
        setIsSaving(true);
        try {
            await database.updateTaskInstance(updatedTask.id, {
                subTasks: updatedTask.subTasks,
                status: updatedTask.status,
                completedAt: updatedTask.completedAt,
                completedBy: updatedTask.completedBy,
                notes: updatedTask.notes || '',
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Failed to autosave task progress:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const toggleStep = (stepIndex: number) => {
        if (task.status === 'completed') {
            toast.error('Cannot modify a finalized task.');
            return;
        }

        const newSubTasks = [...(task.subTasks || [])];
        const step = { ...newSubTasks[stepIndex] };
        
        // If it has sub-steps, toggling the step checkbox should toggle all sub-steps
        if (step.subSteps && step.subSteps.length > 0) {
            const allSelected = step.subSteps.every(ss => ss.done);
            step.subSteps = step.subSteps.map(ss => ({ ...ss, done: !allSelected }));
        }
        
        step.isDone = checkStepCompletion(step);
        newSubTasks[stepIndex] = step;

        const updatedTask = { ...task, subTasks: newSubTasks };
        setTask(updatedTask);
        saveProgress(updatedTask);
    };

    const updateSubStepItemField = (stepIndex: number, subStepIndex: number, field: keyof any, value: any) => {
        if (task.status === 'completed') {
            toast.error('Cannot modify a finalized task.');
            return;
        }

        const newSubTasks = [...(task.subTasks || [])];
        const step = { ...newSubTasks[stepIndex] };
        const newSubSteps = [...(step.subSteps || [])];
        
        newSubSteps[subStepIndex] = {
            ...newSubSteps[subStepIndex],
            [field]: value
        };
        
        // Auto-check if both fields are filled in double field mode? 
        // Maybe not, let user decide.
        
        step.subSteps = newSubSteps;
        step.isDone = checkStepCompletion(step);
        newSubTasks[stepIndex] = step;

        const updatedTask = { ...task, subTasks: newSubTasks };
        setTask(updatedTask);
        saveProgress(updatedTask);
    };

    const toggleSubStepItem = (stepIndex: number, subStepIndex: number) => {
        if (task.status === 'completed') {
            toast.error('Cannot modify a finalized task.');
            return;
        }

        const newSubTasks = [...(task.subTasks || [])];
        const step = { ...newSubTasks[stepIndex] };
        const newSubSteps = [...(step.subSteps || [])];
        
        newSubSteps[subStepIndex] = {
            ...newSubSteps[subStepIndex],
            done: !newSubSteps[subStepIndex].done
        };
        
        step.subSteps = newSubSteps;
        step.isDone = checkStepCompletion(step);
        newSubTasks[stepIndex] = step;

        const updatedTask = { ...task, subTasks: newSubTasks };
        setTask(updatedTask);
        saveProgress(updatedTask);
    };

    const handleFinalize = async () => {
        // Validation
        const missingSteps: { stepLabel: string; subSteps: string[]; stepIndex: number }[] = [];
        
        task.subTasks.forEach((st, sIdx) => {
            const incompleteRequiredSubsteps = (st.subSteps || []).filter(ss => ss.required && !ss.done);
            const isStepIncomplete = st.required && !checkStepCompletion(st);
            
            if (isStepIncomplete || (st.subSteps && incompleteRequiredSubsteps.length > 0)) {
                missingSteps.push({
                    stepLabel: st.label,
                    stepIndex: sIdx + 1,
                    subSteps: incompleteRequiredSubsteps.map((ss, ssIdx) => `${sIdx + 1}.${ssIdx + 1} ${ss.name}`)
                });
            }
        });

        if (missingSteps.length > 0) {
            setShowValidation(true);
            // Expand all steps that have missing items
            const newExpansion = { ...expandedSteps };
            missingSteps.forEach(m => {
                newExpansion[m.stepIndex - 1] = true;
            });
            setExpandedSteps(newExpansion);

            toast.error(({ id }) => (
                <div className="flex flex-col gap-3 max-w-md">
                    <div className="flex items-center gap-2 text-rose-600">
                        <Icons.AlertTriangle className="w-4 h-4" />
                        <span className="font-normal text-xs uppercase tracking-widest leading-none">Incomplete Required Items</span>
                    </div>
                    <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                        {missingSteps.map((m, i) => (
                            <div key={i} className="space-y-1.5">
                                <div className="text-[10px] font-normal text-gray-900 uppercase">
                                    Step {m.stepIndex} - {m.stepLabel}:
                                </div>
                                <ul className="text-[9px] space-y-1 pl-4 text-gray-500 font-normal">
                                    {m.subSteps.length > 0 ? (
                                        m.subSteps.map((ss, j) => <li key={j} className="list-disc">{ss}</li>)
                                    ) : (
                                        <li className="list-disc">Check Step {m.stepIndex} complete</li>
                                    )}
                                </ul>
                            </div>
                        ))}
                    </div>
                    <p className="text-[9px] font-normal text-rose-500 uppercase tracking-tight border-t border-rose-100 pt-2">
                        Please complete all required sub-steps before submitting.
                    </p>
                    <button 
                        onClick={() => toast.dismiss(id)}
                        className="w-full bg-gray-900 text-white p-3 rounded-xl text-[9px] font-normal uppercase tracking-widest hover:bg-black transition-all"
                    >
                        Review Checklist
                    </button>
                </div>
            ), { 
                duration: 8000, 
                style: { 
                    background: 'white', 
                    border: '1px solid #fee2e2', 
                    color: '#991b1b',
                    padding: '20px',
                    borderRadius: '24px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                } 
            });
            return;
        }

        setIsFinalizing(true);
        try {
            const finalizedTask: TaskInstance = {
                ...task,
                status: 'completed',
                completedAt: new Date().toISOString(),
                completedBy: staffName,
                updatedAt: new Date().toISOString()
            };
            
            await database.updateTaskInstance(task.id, finalizedTask);
            setTask(finalizedTask);
            toast.success('Task finalized successfully!');
            onFinalized();
        } catch (error) {
            toast.error('Failed to finalize task.');
        } finally {
            setIsFinalizing(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const completedCount = (task.subTasks || []).filter(st => st.isDone).length;
    const totalCount = (task.subTasks || []).length;
    const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 print:p-0">
            {/* Interactive Desktop View (Hidden on Print) */}
            <div className="print:hidden">
                {/* Header */}
                <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <button 
                            onClick={onBack}
                            className="flex items-center gap-2 text-xs font-normal text-gray-400 hover:text-indigo-600 uppercase tracking-widest transition-colors mb-2"
                        >
                            <Icons.ArrowLeft className="w-3.5 h-3.5" /> Back to Tasks
                        </button>
                        <div className="flex items-center gap-4">
                            <h1 className="text-3xl font-normal text-gray-900 dark:text-white uppercase tracking-tight">{task.title}</h1>
                            {task.status === 'completed' && (
                                <span className="bg-green-100 text-green-700 text-[10px] font-normal px-3 py-1 rounded-full uppercase tracking-widest flex items-center gap-1.5 border border-green-200">
                                    <Icons.Check className="w-3 h-3" /> Finalized
                                </span>
                            )}
                        </div>
                        <p className="text-sm font-normal text-gray-500 uppercase tracking-widest mt-1">
                            Instance for {safeFormat(task.date, 'EEEE, MMMM d, yyyy')}
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        {isSaving && (
                            <div className="flex items-center gap-1.5 text-[10px] font-normal text-indigo-400 uppercase tracking-widest animate-pulse">
                                <Icons.RefreshCw className="w-3 h-3 animate-spin" /> Saving...
                            </div>
                        )}
                        <button 
                            onClick={handlePrint}
                            className="p-3 bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-all text-gray-600 dark:text-gray-400"
                        >
                            <Icons.Printer className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-normal text-gray-400 uppercase tracking-[0.2em]">Overall Progress</span>
                            <span className="text-xs font-normal text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-lg">{percentage}%</span>
                        </div>
                        <span className="text-xs font-normal text-gray-400 uppercase tracking-widest">{completedCount} of {totalCount} Steps</span>
                    </div>
                    <div className="h-3 bg-gray-100 dark:bg-slate-900 rounded-full overflow-hidden border border-gray-200/50 dark:border-slate-800">
                        <div 
                            className={`h-full transition-all duration-700 ease-out rounded-full ${task.status === 'completed' ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.3)]' : 'bg-gradient-to-r from-indigo-500 to-indigo-600 shadow-[0_0_10px_rgba(99,102,241,0.3)]'}`}
                            style={{ width: `${percentage}%` }}
                        />
                    </div>
                </div>

                {/* Checklist */}
                <div className="space-y-0 relative">
                    {/* Vertical Timeline Connector (Desktop Only) */}
                    <div className="absolute left-[39px] top-6 bottom-6 w-0.5 bg-gray-100 dark:bg-slate-800 hidden md:block" />

                    {(task.subTasks || []).map((st, idx) => {
                        const isStepDone = checkStepCompletion(st);
                        const isExpanded = expandedSteps[idx] ?? false;
                        const hasSubSteps = st.subSteps && st.subSteps.length > 0;
                        
                        const incompleteRequiredCount = (st.subSteps || []).filter(ss => ss.required && !ss.done).length;
                        const hasMissingRequiredSubSteps = showValidation && incompleteRequiredCount > 0;
                        
                        return (
                            <div 
                                key={idx}
                                className={`group relative pl-0 md:pl-20 pb-12 last:pb-0 transition-all duration-500`}
                            >
                                {/* Step Indicator (Timeline Badge) */}
                                <div className="hidden md:flex absolute left-0 top-0 items-center justify-center z-10">
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            (task.status !== 'completed' || isAdmin) && toggleStep(idx);
                                        }}
                                        className={`w-20 h-20 rounded-[32px] border-4 flex items-center justify-center transition-all duration-500 shadow-2xl ${
                                            isStepDone 
                                                ? 'bg-green-600 border-green-50 shadow-green-100 dark:shadow-none rotate-[360deg]' 
                                                : hasMissingRequiredSubSteps
                                                    ? 'bg-red-500 border-red-50 text-white animate-bounce'
                                                    : 'bg-white dark:bg-slate-800 border-gray-50 dark:border-slate-800 text-gray-900 dark:text-white hover:scale-110 active:scale-95'
                                        }`}
                                        disabled={task.status === 'completed' && !isAdmin}
                                    >
                                        {isStepDone ? (
                                            <Icons.Check className="w-8 h-8 text-white" />
                                        ) : (
                                            <div className="flex flex-col items-center leading-none">
                                                <span className="text-2xl font-normal">{idx + 1}</span>
                                                <span className="text-[8px] font-normal uppercase tracking-tighter opacity-40">Step</span>
                                            </div>
                                        )}
                                    </button>
                                </div>

                                {/* Step Card Content */}
                                <div 
                                    className={`bg-white dark:bg-slate-800/50 rounded-[40px] p-8 border transition-all duration-500 hover:shadow-2xl hover:shadow-indigo-500/5 ${
                                        isStepDone 
                                            ? 'border-green-100 dark:border-green-900/20 bg-green-50/5 dark:bg-green-900/5' 
                                            : hasMissingRequiredSubSteps 
                                                ? 'border-2 border-red-400 bg-red-50/10' 
                                                : 'border-gray-100 dark:border-slate-800 shadow-xl shadow-gray-500/5'
                                    }`}
                                >
                                    <div 
                                        className={`flex flex-col gap-6 ${hasSubSteps ? 'cursor-pointer select-none' : ''}`} 
                                        onClick={() => hasSubSteps && toggleStepExpansion(idx)}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-3">
                                                    <h3 className={`text-xl md:text-2xl font-normal uppercase tracking-tight transition-all duration-500 ${isStepDone ? 'text-green-700 dark:text-green-400 line-through opacity-40 translate-x-2' : 'text-gray-900 dark:text-white'}`}>
                                                        {st.label}
                                                    </h3>
                                                    {st.required && (
                                                        <div className="relative group/req">
                                                            <div className={`w-2 h-2 rounded-full ${isStepDone ? 'bg-green-300' : 'bg-rose-500 animate-pulse ring-4 ring-rose-500/20'}`} />
                                                            <span className="absolute left-1/2 -bottom-6 -translate-x-1/2 bg-gray-900 text-white text-[8px] font-normal px-2 py-1 rounded-md opacity-0 group-hover/req:opacity-100 transition-opacity whitespace-nowrap pointer-events-none uppercase tracking-widest z-50">Required</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-[10px] font-normal text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                                    Complete the following actions to finish this step.
                                                    {isStepDone && <span className="text-green-500 flex items-center gap-1"><Icons.CheckCircle className="w-3 h-3" /> Step Verified</span>}
                                                </p>
                                            </div>

                                            {hasSubSteps && (
                                                <div className="flex items-center gap-6">
                                                    <div className="flex flex-col items-end">
                                                        <div className="flex items-baseline gap-1">
                                                            <span className="text-lg font-normal text-indigo-600 dark:text-indigo-400">
                                                                {(st.subSteps || []).filter(s => s.done).length}
                                                            </span>
                                                            <span className="text-[10px] font-normal text-gray-300 uppercase tracking-widest">
                                                                / {st.subSteps?.length}
                                                            </span>
                                                        </div>
                                                        <div className="w-20 h-1.5 bg-gray-100 dark:bg-slate-900 rounded-full mt-1 overflow-hidden border border-gray-50 dark:border-slate-800">
                                                            <div 
                                                                className={`h-full transition-all duration-1000 ease-out shadow-[0_0_8px_rgba(99,102,241,0.4)] ${isStepDone ? 'bg-green-500' : 'bg-gradient-to-r from-indigo-500 to-indigo-600'}`} 
                                                                style={{ width: `${Math.round(((st.subSteps || []).filter(s => s.done).length / (st.subSteps?.length || 1)) * 100)}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className={`p-2 rounded-xl transition-all duration-300 ${isExpanded ? 'bg-indigo-50 dark:bg-indigo-900/30 rotate-180' : 'bg-gray-50 dark:bg-slate-900 text-gray-400'}`}>
                                                        <Icons.ChevronDown className="w-5 h-5" />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Sub-steps Content */}
                                        {hasSubSteps && (
                                            <div className={`overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExpanded ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-6 border-t border-gray-100 dark:border-slate-800">
                                                    {st.subSteps?.map((ss, ssIdx) => (
                                                        <div 
                                                            key={ssIdx} 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (ss.useDoubleFields) return; // Inputs handle their own clicks
                                                                (task.status !== 'completed' || isAdmin) && toggleSubStepItem(idx, ssIdx);
                                                            }}
                                                            className={`group/ss flex flex-col gap-3 p-4 rounded-2xl border transition-all duration-300 ${
                                                                !ss.useDoubleFields ? 'cursor-pointer select-none' : ''
                                                            } ${
                                                                ss.done 
                                                                    ? 'bg-green-50/30 border-green-100 dark:bg-green-900/5 dark:border-green-900/20 shadow-inner' 
                                                                    : 'bg-gray-50/50 dark:bg-slate-900/30 border-gray-100 dark:border-slate-800 hover:border-indigo-200 hover:shadow-lg hover:shadow-indigo-500/5 hover:-translate-y-0.5'
                                                            }`}
                                                        >
                                                            <div className="flex items-center gap-4">
                                                                <div className="relative">
                                                                    <button 
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            (task.status !== 'completed' || isAdmin) && toggleSubStepItem(idx, ssIdx);
                                                                        }}
                                                                        className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all duration-300 ${
                                                                            ss.done 
                                                                                ? 'bg-green-600 text-white scale-110 shadow-lg shadow-green-200 dark:shadow-none' 
                                                                                : 'bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 text-transparent group-hover/ss:border-indigo-400'
                                                                        }`}
                                                                    >
                                                                        <Icons.Check className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                                <div className="flex-grow min-w-0">
                                                                    <div className="flex flex-col">
                                                                        <span className={`text-sm font-normal tracking-tight leading-tight transition-all duration-500 ${ss.done ? 'text-green-700 dark:text-green-500 opacity-60 line-through underline-offset-4 decoration-2' : 'text-gray-700 dark:text-gray-200'}`}>
                                                                            {ss.name}
                                                                        </span>
                                                                        {ss.required && !ss.done && (
                                                                            <span className="text-[7px] font-normal text-rose-500 uppercase tracking-widest mt-1">Action Required</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {ss.done && <Icons.CheckCircle className="w-4 h-4 text-green-500 animate-in zoom-in spin-in-12 duration-500" />}
                                                            </div>

                                                            {ss.useDoubleFields && (
                                                                <div className="flex gap-2 pl-10" onClick={e => e.stopPropagation()}>
                                                                    <div className="w-20">
                                                                        <label className="block text-[8px] font-normal text-gray-400 uppercase tracking-widest mb-1 ml-1">Count</label>
                                                                        <input 
                                                                            type="text"
                                                                            value={ss.count || ''}
                                                                            onChange={e => updateSubStepItemField(idx, ssIdx, 'count', e.target.value)}
                                                                            disabled={task.status === 'completed' && !isAdmin}
                                                                            className="w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-2 py-1 text-xs font-normal text-gray-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none text-center"
                                                                            placeholder="0"
                                                                        />
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <label className="block text-[8px] font-normal text-gray-400 uppercase tracking-widest mb-1 ml-1">Details (Room numbers)</label>
                                                                        <input 
                                                                            type="text"
                                                                            value={ss.details || ''}
                                                                            onChange={e => updateSubStepItemField(idx, ssIdx, 'details', e.target.value)}
                                                                            disabled={task.status === 'completed' && !isAdmin}
                                                                            className="w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-1 text-xs font-normal text-gray-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                                                                            placeholder="e.g. 101, 102, 205..."
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Summary/Notes Section */}
                <div className="mt-8 bg-gray-50 dark:bg-slate-900/50 rounded-[32px] p-8 border border-gray-100 dark:border-slate-800">
                    <div className="flex items-center gap-3 mb-6">
                        <Icons.FileText className="w-5 h-5 text-gray-400" />
                        <h3 className="text-xs font-normal uppercase tracking-[0.2em] text-gray-400">Task Summary & Notes</h3>
                    </div>
                    
                    {task.status === 'completed' ? (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-1">
                                    <span className="text-[10px] font-normal text-gray-400 uppercase tracking-widest">Finalized By</span>
                                    <div className="text-sm font-normal text-gray-900 dark:text-white flex items-center gap-2">
                                        <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                                            <Icons.User className="w-3 h-3 text-green-600" />
                                        </div>
                                        {task.completedBy}
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[10px] font-normal text-gray-400 uppercase tracking-widest">Completion Time</span>
                                    <div className="text-sm font-normal text-gray-900 dark:text-white flex items-center gap-2">
                                        <div className="w-6 h-6 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
                                            <Icons.Clock className="w-3 h-3 text-indigo-600" />
                                        </div>
                                        {safeFormat(task.completedAt, 'h:mm a')}
                                    </div>
                                </div>
                            </div>
                            {task.notes && (
                                <div className="pt-6 border-t border-gray-100 dark:border-slate-800">
                                    <span className="block text-[10px] font-normal text-gray-400 uppercase tracking-widest mb-2">Final Notes</span>
                                    <p className="text-sm font-normal text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap italic bg-white dark:bg-slate-800/50 p-4 rounded-xl border border-gray-100 dark:border-slate-800">
                                        "{task.notes}"
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label htmlFor="task-notes" className="block text-[10px] font-normal text-gray-400 uppercase tracking-widest ml-4">Internal Notes</label>
                                <textarea
                                    id="task-notes"
                                    value={task.notes || ''}
                                    onChange={(e) => {
                                        const updated = { ...task, notes: e.target.value };
                                        setTask(updated);
                                        saveProgress(updated);
                                    }}
                                    placeholder="Add internal notes or observations for this task instance..."
                                    className="w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-4 text-sm font-normal focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all min-h-[120px]"
                                    rows={4}
                                />
                            </div>
                            <div className="flex justify-center">
                                <button
                                    onClick={handleFinalize}
                                    disabled={isFinalizing}
                                    className="group relative px-12 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-normal rounded-2xl hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-gray-200 dark:shadow-none uppercase tracking-widest overflow-hidden disabled:opacity-50"
                                >
                                    <span className="relative z-10 flex items-center gap-3">
                                        {isFinalizing ? <Icons.Loader2 className="w-5 h-5 animate-spin" /> : <Icons.ShieldCheck className="w-5 h-5" />}
                                        Finalize Instance
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-violet-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Formal Print Report (Only visible on Print) */}
            <div className="hidden print:block font-serif text-black min-h-screen">
                <div className="text-center mb-6 border-b-2 border-black pb-4">
                    <h1 className="text-xl font-normal uppercase tracking-widest">
                        HOTEL OPERATIONS HUB – SHIFT SUMMARY
                    </h1>
                </div>

                <div className="mb-4 grid grid-cols-1 gap-0.5 text-[10px]">
                    <p><span className="font-normal uppercase tracking-tighter">Date:</span> {safeFormat(task.date, 'EEEE, MMMM d, yyyy')}</p>
                    <p><span className="font-normal uppercase tracking-tighter">Status:</span> {task.status === 'completed' ? 'Finalized' : 'In Progress'}</p>
                    <p><span className="font-normal uppercase tracking-tighter">Progress:</span> {completedCount} / {totalCount} Tasks Completed</p>
                </div>

                <table className="w-full border-collapse border border-black mb-6 text-[9.5px]">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="border border-black p-1 text-left w-10 font-normal uppercase">#</th>
                            <th className="border border-black p-1 text-left font-normal uppercase">Task</th>
                            <th className="border border-black p-1 text-center w-20 font-normal uppercase">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(task.subTasks || []).map((st, idx) => {
                            const stepNum = `1.${idx + 1}`;
                            const isStepDone = checkStepCompletion(st);
                            const hasSubSteps = st.subSteps && st.subSteps.length > 0;
                            
                            return (
                                <React.Fragment key={idx}>
                                    <tr className={`border-b border-black ${hasSubSteps ? 'bg-gray-100/50' : ''}`}>
                                        <td className="border-r border-black p-1 text-black font-normal">{stepNum}</td>
                                        <td className="border-r border-black p-1 font-normal uppercase tracking-tight">
                                            {st.label}
                                        </td>
                                        <td className="p-1 text-center text-sm font-normal">
                                            {isStepDone ? '✔' : ''}
                                        </td>
                                    </tr>
                                    {st.subSteps?.map((ss, ssIdx) => (
                                        <tr key={`${idx}-${ssIdx}`} className="border-b border-black">
                                            <td className="border-r border-black p-1 text-gray-500 pl-2 text-[8px]">
                                                {stepNum}.{ssIdx + 1}
                                            </td>
                                            <td className="border-r border-black p-1 pl-4 text-gray-800">
                                                {ss.name}
                                                {ss.required && !ss.done && <span className="ml-1 text-[6.5px] font-normal text-red-600 uppercase tracking-tighter">[Required]</span>}
                                            </td>
                                            <td className="p-1 text-center text-xs">
                                                {ss.done ? '✔' : '○'}
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>

                {task.notes && (
                    <div className="mb-6 p-2 border border-black italic text-[10px]">
                        <span className="font-normal block not-italic uppercase mb-0.5">Internal Notes:</span>
                        {task.notes}
                    </div>
                )}

                <div className="mt-auto pt-4 border-t border-gray-300 text-[9px] space-y-0.5">
                    <p><span className="font-normal uppercase">Finalized By:</span> {task.completedBy || 'Not yet finalized'}</p>
                    <p><span className="font-normal uppercase">Completion Time:</span> {task.completedAt ? safeFormat(task.completedAt, 'h:mm a') : 'N/A'}</p>
                    <p><span className="font-normal uppercase">Report ID:</span> {task.id}</p>
                    <p><span className="font-normal uppercase">Generated:</span> {safeFormat(new Date(), 'MMMM d, yyyy')}</p>
                </div>
            </div>
        </div>
    );
};

export default TaskExecutionView;
